import { Badge, Button, Card, CardContent, CardTitle, PageHeader, StatusBadge, lotStatusVariant, saleStatusVariant, toast } from '@bia-auction/ui';
import { AlertTriangle, Eye, Timer } from 'lucide-react';
import { t } from '@bia-auction/shared-i18n';
import type { SaleDetail, SaleLot, ServerEvent } from '@bia-auction/shared-schema';
import { saleDetailSchema } from '@bia-auction/shared-schema';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { LotActions } from '../components/LotActions';
import { MaskedPrice } from '../components/MaskedPrice';
import { VehicleThumb } from '../components/VehicleThumb';
import { ErrorMsg, Loading } from '../components/Feedback';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCountdown } from '../lib/format';
import { bypassesDeposit } from '../lib/roles';
import { presentationLabel } from '../lib/labels';
import { fuelLabel, gearLabel, vehicleName } from '../lib/vehicle-labels';
import { useNow } from '../lib/useNow';
import { useSaleRealtime } from '../lib/ws';

/**
 * Login-gated live auction detail (M7; gated again by ADR 0027 after M11 had briefly opened it
 * to visitors) — `GET /auctions/:id` (bidder projection: no reserve)
 * + the sale's realtime stream. Lots are held in local state so WS events update the top bid +
 * countdown live. The full lifecycle contract (`05-realtime-protocol.md`) is handled:
 * `bid.placed`, `lot.opened/closed/extended`, `lot.removed` (+ its `reschedule`), and the
 * sale-level `sale.paused/resumed` (+ resume `reschedule`) / `sale.closed`; `outbid` / `you-won`
 * (user-scoped) raise toasts, and a joint `conference.available` (no `lotId`) surfaces the
 * sale-level join link. On (re)connect — and on every lifecycle event — the REST detail is
 * refetched to reconcile the source of truth.
 */
export function AuctionDetailPage() {
  const { auctionId } = useParams({ from: '/shell/auctions/$auctionId' });
  const { state } = useAuth();
  const qc = useQueryClient();
  const now = useNow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['auction', auctionId],
    queryFn: () => api<SaleDetail>(`/auctions/${auctionId}`, {}, saleDetailSchema),
  });

  // WS-delivered updates are held as per-lot **overlays merged over** the REST snapshot at
  // render time (below), not mutated into a copy of it. A refetch that read D1 just before a
  // bid committed can otherwise land *after* the corresponding WS frame and clobber the newer
  // top bid/count; keeping overlays separate lets the realtime truth win over a stale snapshot.
  const [overlays, setOverlays] = useState<Record<string, Partial<SaleLot>>>({});
  const patchLot = useCallback((lotId: string, patch: Partial<SaleLot>) => {
    setOverlays((o) => ({ ...o, [lotId]: { ...o[lotId], ...patch } }));
  }, []);
  // Apply a reschedule payload (pause/resume/remove recompute the serial tail) to the overlays
  // immediately, so countdowns don't stay stale while the detail refetches.
  const applyReschedule = useCallback(
    (entries: { lotId: string; newStartDate: number; newEndDate: number }[]) => {
      setOverlays((o) => {
        const next = { ...o };
        for (const e of entries) {
          next[e.lotId] = { ...next[e.lotId], startDate: e.newStartDate, endDate: e.newEndDate };
        }
        return next;
      });
    },
    [],
  );
  const refetch = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['auction', auctionId] });
  }, [qc, auctionId]);

  // A (re)connect is the reconciliation point: the client has just re-subscribed, so the fresh
  // snapshot is authoritative and reflects anything missed during an outage. **Clear the
  // overlays** here so a stale overlay (e.g. a bid at 100) can't keep hiding a newer snapshot
  // value (a missed bid at 200) indefinitely; events after reconnect re-overlay on top. Live
  // (non-reconnect) lifecycle refetches keep overlays — that's what lets a just-received bid win
  // over a refetch that read D1 before it committed.
  const reconcileOnReconnect = useCallback(() => {
    setOverlays({});
    refetch();
  }, [refetch]);

  const onEvent = useCallback(
    (ev: ServerEvent) => {
      switch (ev.type) {
        case 'bid.placed':
          patchLot(ev.lotId, {
            currentTopBid: ev.amount,
            bidCount: ev.bidCount,
            reserveCrossed: ev.priceGoalReached,
            // The step is not constant — it changes across a band boundary and again past the
            // reserve. Without carrying it the panel would keep computing the next figure from
            // the REST snapshot's step, and every +1 would be rejected as stale (M19).
            ...(ev.nextIncrement !== undefined ? { increment: ev.nextIncrement } : {}),
          });
          break;
        case 'lot.opened':
          patchLot(ev.lotId, { state: 'OPEN', endDate: ev.endsAt });
          break;
        case 'lot.extended':
          patchLot(ev.lotId, { endDate: ev.newEndsAt });
          break;
        case 'lot.closed':
          patchLot(ev.lotId, { state: ev.finalState });
          break;
        case 'lot.removed':
          patchLot(ev.lotId, { state: 'REMOVED' });
          applyReschedule(ev.reschedule); // shift the pulled-earlier siblings
          refetch();
          break;
        case 'sale.paused':
        case 'sale.closed':
          // Refetch so data.state (→ PAUSED/CLOSED) gates the controls + banner below.
          refetch();
          break;
        case 'sale.resumed':
          applyReschedule(ev.reschedule); // new deadlines for the resumed window
          refetch();
          break;
        case 'outbid':
          toast(t('bidder.detail.outbid'), 'error');
          break;
        case 'you-won':
          toast(t('bidder.detail.won'), 'success');
          break;
        case 'conference.available':
          if (ev.lotId) {
            patchLot(ev.lotId, {
              conference: { provider: null, roomId: null, joinUrl: ev.joinUrl, hostUrl: null, provisionedAt: ev.availableAt },
            });
          } else {
            // Joint (sale-level) room — no lotId. Refetch so data.conference.joinUrl appears.
            refetch();
          }
          break;
      }
    },
    [patchLot, applyReschedule, refetch],
  );

  // The displayed lots: the REST snapshot with each lot's WS overlay applied on top (overlays
  // are the newest truth, so they survive a stale refetch).
  const lots = useMemo(
    () => (data?.lots ?? []).map((l) => (overlays[l.id] ? { ...l, ...overlays[l.id] } : l)),
    [data, overlays],
  );

  // Stable room list keyed on the lot ids — `useNow` re-renders every second, and a fresh array
  // each render would make the WS effect resend a full `subscribe` frame every tick.
  const lotIdKey = lots.map((l) => l.id).join(',');
  const lotRooms = useMemo(
    () => (lotIdKey ? lotIdKey.split(',').map((id) => `lot:${id}`) : []),
    [lotIdKey],
  );
  // Fingerprint of the authenticated identity + roles — a change (e.g. a refresh that downgraded
  // the viewer) re-opens the socket so it re-verifies the fresh token at the upgrade, dropping a
  // connection established with now-revoked privileges.
  const authKey = `${state.userId ?? ''}|${[...state.roles].sort().join(',')}`;
  // Still gated on the session even though the route now requires one (ADR 0027): the socket
  // authenticates at the upgrade, so opening it without a token only produces a rejected
  // handshake and a reconnect loop. Belt to the router's braces.
  useSaleRealtime(
    state.authenticated ? auctionId : null,
    lotRooms,
    onEvent,
    reconcileOnReconnect,
    authKey,
  );

  if (isLoading) return <Loading />;
  if (error) return <ErrorMsg error={error} />;
  if (!data) return null;

  const salePaused = data.state === 'PAUSED';
  const saleEnded = data.state === 'CLOSED' || data.state === 'CANCELLED';
  const saleActive = !salePaused && !saleEnded;

  return (
    <div>
      <Link to="/" className="text-sm text-muted-foreground hover:underline">
        {t('bidder.detail.back')}
      </Link>
      <PageHeader
        className="mt-2 mb-3"
        title={data.title ?? t('sales.untitled')}
        subline={
          <span className="flex items-center gap-2">
            {data.location}
            <StatusBadge variant={saleStatusVariant(data.state)}>
              {presentationLabel(data.presentationStatus)}
            </StatusBadge>
          </span>
        }
      />
      {data.conference?.joinUrl && (
        <a
          href={data.conference.joinUrl}
          target="_blank"
          rel="noreferrer"
          className="mb-3 inline-block text-tx-brand hover:underline"
        >
          {t('bidder.detail.joinConf')}
        </a>
      )}
      {salePaused && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-warning-tint px-3 py-2 text-sm text-warning-tint-fg">
          <AlertTriangle aria-hidden className="h-4 w-4 shrink-0" />
          {t('presentation.paused')}
        </div>
      )}
      {/* A single full-width column, in `orderIndex` order (the API sorts; this keeps it).
          A two-column grid read 1,2 / 3,4 across the rows, which is NOT how an auction runs
          through its lots — the order has to be unambiguous down the page. */}
      <div className="flex flex-col gap-3">
        {lots
          .filter((l) => l.state !== 'REMOVED')
          .map((lot) => (
            <LotCard
              key={lot.id}
              saleId={auctionId}
              lot={lot}
              now={now}
              saleActive={saleActive}
              canDeposit={!bypassesDeposit(state.roles)}
              authenticated={state.authenticated}
            />
          ))}
      </div>
    </div>
  );
}

function LotCard({
  saleId,
  lot,
  now,
  saleActive,
  canDeposit,
  authenticated,
}: {
  saleId: string;
  lot: SaleLot;
  now: number;
  saleActive: boolean;
  canDeposit: boolean;
  authenticated: boolean;
}) {
  const remaining = lot.endDate - now;
  // A paused (or closed/cancelled) sale is not accepting bids even if the lot's cached endDate
  // hasn't passed — gate the open state on the live sale state too.
  const isOpen = saleActive && lot.state === 'OPEN' && remaining > 0;
  const closed = lot.state === 'SOLD' || lot.state === 'UNSOLD';

  return (
    /* The live lot is the one thing on this page that must be findable without reading: it
       gets the success tint, a ring, and roughly double the row height of a queued lot. */
    <Card
      className={
        isOpen ? 'border-success bg-success-tint ring-1 ring-success' : undefined
      }
    >
      <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-stretch">
        {/* Photo — the reason a bidder stops on a row at all, so it is sized to be looked at
            rather than acknowledged. The open lot's is doubled again. */}
        <VehicleThumb
          url={lot.vehicle?.coverUrl}
          plate={lot.plateSnapshot}
          className={
            isOpen
              ? 'h-52 w-full sm:h-64 sm:w-80'
              : 'h-40 w-full sm:h-32 sm:w-48'
          }
        />

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {/* M11: the row is titled by what the car IS, with the plate as a subline. */}
              <CardTitle className={isOpen ? 'text-lg' : 'text-base'}>
                {/* The lot's running order, stated — with 50 lots down one column, "which car
                    is next" must not be a counting exercise. */}
                <span className="mr-2 text-muted-foreground tabular-nums">
                  {lot.orderIndex + 1}.
                </span>
                <Link
                  to="/auctions/$auctionId/lots/$lotId"
                  params={{ auctionId: saleId, lotId: lot.id }}
                  className="hover:underline"
                >
                  {lot.vehicle
                    ? vehicleName({ ...lot.vehicle, plate: lot.plateSnapshot })
                    : lot.plateSnapshot}
                </Link>
              </CardTitle>
              {lot.plateSnapshot && (
                <p className="font-mono text-xs text-muted-foreground tabular-nums">
                  {lot.plateSnapshot}
                </p>
              )}
              {lot.vehicle && (
                <p className="text-xs text-muted-foreground">
                  {[
                    lot.vehicle.year,
                    lot.vehicle.km !== null ? `${lot.vehicle.km.toLocaleString('tr-TR')} km` : null,
                    lot.vehicle.fuel ? fuelLabel(lot.vehicle.fuel) : null,
                    lot.vehicle.gear ? gearLabel(lot.vehicle.gear) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-1">
              {isOpen && <StatusBadge variant="live">{t('lotState.OPEN')}</StatusBadge>}
              {lot.saleMode === 'guaranteed' && (
                <Badge variant="secondary">{t('bidder.detail.guaranteed')}</Badge>
              )}
              {lot.reserveCrossed && (
                <StatusBadge variant="success">{t('bidder.detail.reserveMet')}</StatusBadge>
              )}
              {closed && (
                <StatusBadge variant={lotStatusVariant(lot.state)}>
                  {t(`lotState.${lot.state}`)}
                </StatusBadge>
              )}
            </div>
          </div>

          {/* Price hierarchy (approved OQ-2): tabular current bid, condensed meta row; the
              countdown escalates to warning (colour + icon) under 30s. */}
          <MaskedPrice
            amount={lot.currentTopBid ?? lot.startingPrice}
            masked={lot.priceMasked}
            currency={lot.currency}
            className={`mt-1 block font-head font-semibold tabular-nums ${
              isOpen ? 'text-display' : 'text-h1'
            }`}
          />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="tabular-nums">
              {t('bidder.detail.bidCountShort', { n: lot.bidCount })}
            </span>
            {isOpen && (
              <span
                className={
                  remaining < 30_000
                    ? 'flex items-center gap-1 font-medium text-warning'
                    : 'flex items-center gap-1'
                }
              >
                <Timer aria-hidden className="h-4 w-4" />
                <span className="font-mono tabular-nums">
                  {t('bidder.detail.remainingShort', { time: formatCountdown(remaining) })}
                </span>
              </span>
            )}
            {lot.conference?.joinUrl && (
              <a
                href={lot.conference.joinUrl}
                target="_blank"
                rel="noreferrer"
                className="text-tx-brand hover:underline"
              >
                {t('bidder.detail.joinConf')}
              </a>
            )}
          </div>
        </div>

        {/* Every action in one right-hand column, so the eye scans cars down the middle and
            acts down the edge. "İncele" stays available for a closed lot — it has no bid
            control to press, but the car is still worth looking at. */}
        {/* Queued lots pack their controls two-up: five stacked buttons would make every row as
            tall as the live one, and the live row's extra height is the whole point. */}
        <div
          className={
            isOpen
              ? 'flex w-full shrink-0 flex-col gap-2 sm:w-56 sm:justify-center'
              : 'grid w-full shrink-0 grid-cols-2 gap-2 self-center sm:w-64'
          }
        >
          <Button asChild variant="outline" className="h-11 w-full">
            <Link to="/auctions/$auctionId/lots/$lotId" params={{ auctionId: saleId, lotId: lot.id }}>
              <Eye aria-hidden className="h-4 w-4" />
              {t('bidder.detail.view')}
            </Link>
          </Button>
          <LotActions
            saleId={saleId}
            lot={lot}
            isOpen={isOpen}
            saleActive={saleActive}
            canDeposit={canDeposit}
            authenticated={authenticated}
            stacked
          />
        </div>
      </CardContent>
    </Card>
  );
}
